//////////////////////////////////////////////////////////////////////////////
//
//  React Query (TanStack Query) 설정
//
//  작성자: MediInStep Team
//  생성일: 2025-01-XX
//  수정일: 2025-01-XX
//
//  설명: QueryClient 설정 및 기본 옵션
//
//////////////////////////////////////////////////////////////////////////////

import { QueryClient } from '@tanstack/react-query';

/**
 * QueryClient 인스턴스
 *
 * 기본 설정:
 * - 재시도: 2번 (총 3번 시도)
 * - 타임아웃: 10초
 * - 캐싱: 5분
 * - Exponential Backoff
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ========== 캐싱 설정 ==========
      staleTime: 5 * 60 * 1000, // 5분 - 이 시간 동안은 fresh 상태 (재요청 안 함)
      gcTime: 10 * 60 * 1000, // 10분 - 메모리에 캐시 유지 시간 (구 cacheTime)

      // ========== 재시도 설정 ==========
      retry: 2, // 실패 시 2번 재시도 (총 3번 시도)
      retryDelay: (attemptIndex) => {
        // Exponential Backoff: 1초, 2초, 4초...
        return Math.min(1000 * 2 ** attemptIndex, 30000);
      },

      // ========== 리페치 설정 ==========
      refetchOnWindowFocus: true, // 화면 포커스 시 자동 새로고침
      refetchOnReconnect: true, // 네트워크 재연결 시 자동 새로고침
      refetchOnMount: true, // 컴포넌트 마운트 시 새로고침

      // ========== 네트워크 설정 ==========
      networkMode: 'online', // 온라인일 때만 요청 ('always', 'online', 'offlineFirst')
    },

    mutations: {
      // ========== 재시도 설정 (조건부) ==========
      retry: (failureCount, error: any) => {
        // 재시도하면 안 되는 HTTP 상태 코드
        const noRetryStatuses = [400, 401, 403, 404, 422];
        const status = error?.response?.status;

        if (status && noRetryStatuses.includes(status)) {
          console.log(`⚠️ [Mutation] 재시도 불가 (${status})`);
          return false; // 재시도 안 함
        }

        // 나머지는 2번까지 재시도
        return failureCount < 2;
      },

      retryDelay: (attemptIndex) => {
        return Math.min(1000 * 2 ** attemptIndex, 30000);
      },

      // ========== 전역 에러 핸들러 ==========
      onError: (error: any, variables, context) => {
        console.error('🚨 [Mutation Error]:', {
          error: error?.message || error,
          status: error?.response?.status,
          variables,
        });

        // ℹ️ 401/403 인증 오류는 tokenRefreshHandler에서 처리됨
        // ℹ️ Axios Interceptor가 자동으로 Token Refresh 시도
        // ℹ️ Refresh 실패 시 performLogout() 호출하여 로그아웃

        // 여기서는 다른 에러들만 처리
        const status = error?.response?.status;

        if (status && status >= 500) {
          console.error('🚨 [Mutation] 서버 에러 (500+)');
        } else if (status === 400) {
          console.error('🚨 [Mutation] 잘못된 요청 (400)');
        } else if (status === 404) {
          console.error('🚨 [Mutation] 리소스를 찾을 수 없음 (404)');
        } else if (status === 422) {
          console.error('🚨 [Mutation] 유효성 검증 실패 (422)');
        }
      },

      // ========== 네트워크 설정 ==========
      networkMode: 'online',
    },
  },
});

/**
 * QueryClient 초기화 (필요시)
 */
export const initializeQueryClient = () => {
  console.log('🔄 React Query initialized');
  console.log('  - Retry: 2회');
  console.log('  - Stale Time: 5분');
  console.log('  - GC Time: 10분');
  console.log('  - Exponential Backoff 활성화');
};














